import { type JSX } from "react";
import { Image, RichText, Text } from "@sitecore-content-sdk/nextjs";
import { TvdProps } from "components/TVD/tvd.props";
import { fetchTvdData, type TvdMonetaryData } from "../../lib/tvd";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";

type MonetaryKey = keyof TvdMonetaryData;

const MONETARY_KEYS: readonly MonetaryKey[] = [
  "business_outcomes",
  "operational_performance",
  "environmental_impact",
];

const isMonetaryKey = (value: string): value is MonetaryKey =>
  (MONETARY_KEYS as readonly string[]).includes(value);

const normalizeCardName = (name: string): string =>
  name.toLowerCase().replaceAll(/\s+/g, "_");

interface TvdStyleSet {
  headerTitleStyle: string;
  subTitleStyle: string;
  titleTextStyle: string;
  monetaryTextStyle: string;
  descriptionStyle: string;
  cardStyle: string;
}

const getStyles = (cardBackground?: string): TvdStyleSet => {
  const isBlue = cardBackground === "bg-ecolab-blue";

  return {
    headerTitleStyle: isBlue
      ? "font-bold text-[44px] tracking-[0.4px] leading-[66px]"
      : "font-medium text-[28px] tracking-[0.4px] leading-[42px] lg:text-[32px] lg:tracking-[0.8px] lg:leading-[40px]",
    subTitleStyle: isBlue
      ? "text-xl tracking-[0.4px] lg:tracking-[0.8px] leading-[22px]"
      : "text-2xl font-medium leading-[26.4px]",
    titleTextStyle: isBlue
      ? "text-white font-bold text-[28px] tracking-[0.4px] leading-[42px] lg:text-[32px] lg:tracking-[0.8px] lg:leading-[48px]"
      : "text-black text-2xl font-medium leading-[26.4px]",
    monetaryTextStyle: isBlue ? "text-white" : "text-ecolab-blue",
    descriptionStyle: isBlue
      ? "text-white [&_*]:text-white text-lg leading-[27px]"
      : "text-text-gray [&_*]:text-text-gray text-base leading-[24px]",
    cardStyle: isBlue
      ? "bg-ecolab-blue pt-[2px]"
      : "rounded-lg shadow-md bg-white",
  };
};

const getComponentTextColor = (containerStyles?: string): string =>
  containerStyles?.includes("bg-white") ? "text-ecolab-blue" : "text-white";

export const DefaultBlueCardWhiteText = async (props: TvdProps): Promise<JSX.Element> => {
  console.log("---->[TVD] Component", props.fields);

  const { page, fields, params } = props;
  const datasource = fields?.data?.datasource;

  if (!datasource) {
    return <NoDataFallback componentName="TVD" />;
  }

  const isPageEditing = page.mode.isEditing;
  const titleField = datasource.Title?.jsonValue;
  const subTitleField = datasource.Subtitle?.jsonValue;
  const footerTextField = datasource.FooterText?.jsonValue;
  const tvdCards = datasource.tvdContentCard?.tvdContentCardResult ?? [];
  const cardBackground = params?.CardBackground || "bg-ecolab-blue";
  const styles = getStyles(cardBackground);
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const componentTextColor = "text-white";

  // Fetch monetary data server-side at request time. Errors fall back to
  // empty values so the cards still render with title/description only.
  let monetaryData: TvdMonetaryData | null = null;
  try {
    const response = await fetchTvdData();
    monetaryData = response.data;
  } catch (error) {
    console.error("[TVD] Failed to fetch monetary data:", error);
  }

  return (
    <div
      className={`tvd-component pt-[var(--global-component-spacing)] pb-[var(--global-component-spacing)] px-[var(--spacing-sixteen)] m-auto min-h-[1px] ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      data-testid="tvd-card"
      data-role="tvd-component"
    >
      <div className="component-content">
        <div className="card-header pb-[10px]">
          {(titleField?.value || isPageEditing) && (
            <Text
              tag="h2"
              field={titleField}
              className={`m-0 pb-[20px] text-center ${componentTextColor} ${styles.headerTitleStyle}`}
              data-testid="header-title"
            />
          )}
          {(subTitleField?.value || isPageEditing) && (
            <Text
              tag="h3"
              field={subTitleField}
              className={`${componentTextColor} text-center ${styles.subTitleStyle}`}
              data-testid="sub-header"
            />
          )}
        </div>
        <div className="card-content grid grid-cols-1 lg:grid-cols-3 gap-8 mx-auto  min-h-[580px] min-md:min-h-[687px] min-lg:min-h-[579px] [&:has(:not(img))]:min-h-auto!">
          {tvdCards.map((item) => {
            const formattedName = normalizeCardName(item.name);
            const monetaryField = isMonetaryKey(formattedName)
              ? monetaryData?.[formattedName]
              : undefined;

            return (
              <div
                key={formattedName}
                className={`overflow-hidden w-full ${styles.cardStyle} ${cardBackground ?? ""}`}
                data-testid="card-items"
              >
                {(item?.Image?.jsonValue?.value?.src || isPageEditing) && (
                  <div className="min-h-[202px] min-md:min-h-[415px] min-lg:min-h-[253px]">
                    <Image
                      field={item?.Image?.jsonValue}
                      className="w-full object-contain"
                      data-testid="card-image"
                      imageParams={{ mw: 1200 }}
                    />
                  </div>
                )}
                <div className="p-5">
                  {(item?.Title?.jsonValue?.value || isPageEditing) && (
                    <Text
                      tag="h3"
                      field={item?.Title?.jsonValue}
                      className={`text-center! ${styles.titleTextStyle}`}
                      data-testid="card-title"
                    />
                  )}
                  {monetaryField?.value && (
                    <RichText
                      tag="h3"
                      field={monetaryField}
                      className={`text-[46px] font-bold text-center **:text-center leading-[50.6px] ${styles.monetaryTextStyle}`}
                      data-testid="card-monetaryvalue"
                    />
                  )}
                  {(item?.Description?.jsonValue?.value || isPageEditing) && (
                    <RichText
                      tag="p"
                      field={item?.Description?.jsonValue}
                      data-testid="card-description"
                      className={`text-center **:text-center tracking-[0.4px] lg:tracking-[0.8px] mb-[var(--spacing-thirtytwo)] ${styles.descriptionStyle}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {(footerTextField?.value || isPageEditing) && (
          <div className="card-footer mt-[var(--global-component-spacing)]">
            <Text
              tag="p"
              field={footerTextField}
              className={`${componentTextColor} tracking-[0.4px] lg:tracking-[0.8px] leading-[24px] text-center`}
              data-testid="card-footertext"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const WhiteCardBlueText = async (props: TvdProps): Promise<JSX.Element> => {
  console.log("---->[TVD] Component", props.fields);

  const { page, fields, params } = props;
  const datasource = fields?.data?.datasource;

  if (!datasource) {
    return <NoDataFallback componentName="TVD" />;
  }

  const isPageEditing = page.mode.isEditing;
  const titleField = datasource.Title?.jsonValue;
  const subTitleField = datasource.Subtitle?.jsonValue;
  const footerTextField = datasource.FooterText?.jsonValue;
  const tvdCards = datasource.tvdContentCard?.tvdContentCardResult ?? [];
  const cardBackground = params?.CardBackground || "bg-ecolab-blue";
  const styles = getStyles(cardBackground);
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const componentStyles = params?.Styles ? `!${params.Styles}` : "!bg-white";
  const componentTextColor = getComponentTextColor(componentStyles);

  // Fetch monetary data server-side at request time. Errors fall back to
  // empty values so the cards still render with title/description only.
  let monetaryData: TvdMonetaryData | null = null;
  try {
    const response = await fetchTvdData();
    monetaryData = response.data;
  } catch (error) {
    console.error("[TVD] Failed to fetch monetary data:", error);
  }

  return (
    <div
      className={`tvd-component pt-[var(--global-component-spacing)] pb-[var(--global-component-spacing)] px-[var(--spacing-sixteen)] m-auto min-h-[1px] ${componentStyles} ${hiddenBlockStyles}`}
      data-testid="tvd-card"
      data-role="tvd-component"
    >
      <div className="component-content">
        <div className="card-header pb-[10px]">
          {(titleField?.value || isPageEditing) && (
            <Text
              tag="h2"
              field={titleField}
              className={`m-0 pb-[20px] text-center ${componentTextColor} ${styles.headerTitleStyle}`}
              data-testid="header-title"
            />
          )}
          {(subTitleField?.value || isPageEditing) && (
            <Text
              tag="h3"
              field={subTitleField}
              className={`${componentTextColor} text-center ${styles.subTitleStyle}`}
              data-testid="sub-header"
            />
          )}
        </div>
        <div className="card-content grid grid-cols-1 lg:grid-cols-3 gap-8 mx-auto  min-h-[580px] min-md:min-h-[687px] min-lg:min-h-[579px] [&:has(:not(img))]:min-h-auto!">
          {tvdCards.map((item) => {
            const formattedName = normalizeCardName(item.name);
            const monetaryField = isMonetaryKey(formattedName)
              ? monetaryData?.[formattedName]
              : undefined;

            return (
              <div
                key={formattedName}
                className={`overflow-hidden w-full ${styles.cardStyle} ${cardBackground ?? ""}`}
                data-testid="card-items"
              >
                {(item?.Image?.jsonValue?.value?.src || isPageEditing) && (
                  <div className="min-h-[202px] min-md:min-h-[415px] min-lg:min-h-[253px]">
                    <Image
                      field={item?.Image?.jsonValue}
                      className="w-full object-contain"
                      data-testid="card-image"
                      imageParams={{ mw: 1200 }}
                    />
                  </div>
                )}
                <div className="p-5">
                  {(item?.Title?.jsonValue?.value || isPageEditing) && (
                    <Text
                      tag="h3"
                      field={item?.Title?.jsonValue}
                      className={`text-center! ${styles.titleTextStyle}`}
                      data-testid="card-title"
                    />
                  )}
                  {monetaryField?.value && (
                    <RichText
                      tag="h3"
                      field={monetaryField}
                      className={`text-[46px] font-bold text-center **:text-center leading-[50.6px] ${styles.monetaryTextStyle}`}
                      data-testid="card-monetaryvalue"
                    />
                  )}
                  {(item?.Description?.jsonValue?.value || isPageEditing) && (
                    <RichText
                      tag="p"
                      field={item?.Description?.jsonValue}
                      data-testid="card-description"
                      className={`text-center **:text-center tracking-[0.4px] lg:tracking-[0.8px] mb-[var(--spacing-thirtytwo)] ${styles.descriptionStyle}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {(footerTextField?.value || isPageEditing) && (
          <div className="card-footer mt-[var(--global-component-spacing)]">
            <Text
              tag="p"
              field={footerTextField}
              className={`${componentTextColor} tracking-[0.4px] lg:tracking-[0.8px] leading-[24px] text-center`}
              data-testid="card-footertext"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const Default = DefaultBlueCardWhiteText;